import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { triggerLLMGuardModal } from "@/lib/useLLMGuardState";
import { STUDIO_FACES, STUDIO_LAYOUT, cropStyle } from "@/lib/siloCubeFaces";
import { edgeRenderStyle, edgeAmountDefault, edgeVarianceDefault, KNOWN_EDGES } from "@/lib/edgeStyles";
import SiloFaceFramer from "@/components/SiloFaceFramer";
import SiloArtStage2Panel from "@/components/SiloArtStage2Panel";
import EdgeControls from "@/components/EdgeControls";
import CubeNavControls from "@/components/CubeNavControls";
import { Sparkle, UploadSimple, Microphone, MagicWand, X, Spinner, PaintBrush, CheckCircle, CaretUp, Minus } from "@phosphor-icons/react";
import "../styles/siloCube.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// SiloImageStudio — the full-screen photo-setup tool (the ONLY full-screen
// surface). Controls live up top; the scrollable "square system" of faces sits
// at the bottom — tap a square to choose where the image goes. AI runs on the
// creator's own key; uploads come from the phone. This is opened by
// tap-and-hold on a silo (mobile) or the tiny "−" (web), never for navigation.
export default function SiloImageStudio({ silo, open, onClose, isFounder, onSaved }) {
  const [mode, setMode] = useState("ai");
  const [studioStage, setStudioStage] = useState(() => {
    try { return sessionStorage.getItem("mysilo_studio_stage") === "stage2" ? "stage2" : "stage1"; } catch { return "stage1"; }
  });
  const [models, setModels] = useState([]);
  const [edges, setEdges] = useState([]);
  const [modelId, setModelId] = useState("");
  const [edgeStyle, setEdgeStyle] = useState("clean");
  const [edgeAmount, setEdgeAmount] = useState(edgeAmountDefault("clean"));
  const [edgeVariance, setEdgeVariance] = useState(edgeVarianceDefault("clean"));
  const [deviation, setDeviation] = useState(40);
  const [description, setDescription] = useState(() => {
    // Jul 2026 (Randy) — draft persistence upgraded sessionStorage →
    // localStorage so the nudge survives a full browser close. Old
    // sessionStorage value read once as a legacy fallback.
    try {
      return localStorage.getItem(`mysilo:draft:cube_describe:${silo?.id}`)
        ?? sessionStorage.getItem(`cube_describe:${silo?.id}`) ?? "";
    } catch { return ""; }
  });
  const [targetFace, setTargetFace] = useState("center");
  const [reuseAll, setReuseAll] = useState(false);
  const [alwaysLabel, setAlwaysLabel] = useState(!!silo?.cube?.always_show_label);
  const [savingLabel, setSavingLabel] = useState(false);
  const [genAll, setGenAll] = useState(false);
  const [dressConfirmOpen, setDressConfirmOpen] = useState(false);
  const [genAllProgress, setGenAllProgress] = useState({ done: 0, total: 10 });
  const [describeOpen, setDescribeOpen] = useState(false);
  // keep the typed/dictated description around between studio opens (per silo)
  useEffect(() => {
    try { if (silo?.id) localStorage.setItem(`mysilo:draft:cube_describe:${silo.id}`, description); } catch { /* */ }
  }, [description, silo?.id]);
  // Remember which studio tab (Stage 1 / Stage 2) was last open, so re-entering
  // the cube studio lands you back where you were.
  useEffect(() => {
    try { sessionStorage.setItem("mysilo_studio_stage", studioStage); } catch { /* */ }
  }, [studioStage]);
  const [drafting, setDrafting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const [localFaces, setLocalFaces] = useState(silo?.cube?.faces || {});
  const [bust, setBust] = useState(Date.now());
  const [framer, setFramer] = useState(null);
  const [framerUploading, setFramerUploading] = useState(false);
  const [framerRecreating, setFramerRecreating] = useState(false);
  const [facesCollapsed, setFacesCollapsed] = useState(false);
  const [fillOnly, setFillOnly] = useState(false);
  const fileRef = useRef(null);
  const framerFileRef = useRef(null);
  const recogRef = useRef(null);
  const pollRef = useRef(null);
  const dressSnapRef = useRef(null);
  // Feb 28 2026 — hardening: `stopPoll` was referenced by the
  // generate-all polling flow but never defined; blocked lint. Simple
  // wrapper that clears the interval + nulls the ref so subsequent
  // starts are safe.
  const stopPoll = () => {
    if (pollRef.current) {
      try { clearInterval(pollRef.current); } catch { /* noop */ }
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/me/image-models?silo_id=${encodeURIComponent(silo?.id || "")}`);
        if (cancelled) return;
        const ms = r.data?.models || [];
        setModels(ms);
        setEdges(r.data?.edges || []);
        const firstAvail = ms.find((m) => m.available) || ms[0];
        if (firstAvail) setModelId((cur) => cur || firstAvail.id);
      } catch {
        toast.error("Couldn't load the image options.");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const selectedModel = models.find((m) => m.id === modelId);
  const modelUnavailable = selectedModel && !selectedModel.available;
  const faceLabel = (STUDIO_FACES.find((f) => f.id === targetFace) || {}).label || "Center";
  // Jun 2026 (Randy) — non-founders never get the founder-only "Admin" face,
  // so the whole-cube dress hits 15 faces for them (16 only for super admin).
  const effectiveFaces = isFounder ? STUDIO_FACES : STUDIO_FACES.filter((f) => !f.founderOnly);
  const faceUrl = (id) => {
    const imgId = localFaces?.[id]?.image_id;
    return imgId ? `${BACKEND_URL}/api/silo-images/${imgId}?t=${bust}` : null;
  };

  const applyResult = (imageId, all = false) => {
    setLocalFaces((prev) => {
      const next = { ...prev };
      if (all) effectiveFaces.forEach((f) => { next[f.id] = { image_id: imageId, edge_style: edgeStyle }; });
      else next[targetFace] = { image_id: imageId, edge_style: edgeStyle };
      return next;
    });
    setBust(Date.now());
    onSaved && onSaved();
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice typing isn't supported in this browser. Please type instead."); return; }
    if (listening) { recogRef.current?.stop(); return; }
    const r = new SR();
    r.lang = "en-US"; r.interimResults = false; r.continuous = true;
    r.onresult = (e) => {
      let t = "";
      for (let i = e.resultIndex; i < e.results.length; i++) t += e.results[i][0].transcript;
      const add = t.trim();
      if (add) setDescription((d) => (d ? d + " " : "") + add);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
    try { r.start(); setListening(true); } catch { setListening(false); }
  };

  const draftFromTier1 = async () => {
    setDrafting(true);
    try {
      const r = await api.post(`/silos/${silo.id}/image/describe`);
      setDescription(r.data?.description || "");
      toast.success("Drafted a description from your Tier 1.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft a description.");
    } finally { setDrafting(false); }
  };

  const popImageGuard = () => {
    const prov = selectedModel?.provider || "gemini";
    const consoleUrl = prov === "openai" ? "https://platform.openai.com/" : "https://aistudio.google.com/app/apikey";
    triggerLLMGuardModal({ provider: prov, surface: "silo_art", consoleUrl });
  };

  const generate = async () => {
    if (!modelId) { toast.error("Pick an image model first."); return; }
    if (modelUnavailable) { popImageGuard(); return; }
    setGenerating(true);
    const snap = snapshotFaces([targetFace]);
    try {
      // Jul 2026 (Randy) — Describe is the PRIORITY source. If it's empty,
      // silently run the same quick silo review as "Draft from my Tier 1"
      // to get a nudge, then create the face in one motion.
      let prompt = description;
      if (!prompt.trim()) {
        try {
          const d = await api.post(`/silos/${silo.id}/image/describe`);
          prompt = (d.data?.description || "").trim();
          if (prompt) {
            setDescription(prompt);
            toast("No description given — drafted one from your silo and creating with it.", { duration: 5000 });
          }
        } catch { /* fall through — generate with a blank prompt as before */ }
      }
      const r = await api.post(`/silos/${silo.id}/image/generate`, {
        prompt, model: modelId, edge_style: edgeStyle, deviation, face: targetFace, reuse_for_all: false,
      });
      applyResult(r.data?.image_id, false);
      toast.success(`Image set on the ${faceLabel} face.`, { action: { label: "Undo", onClick: () => restoreFaces(snap) } });
    } catch (e) {
      if (e?.response?.data?.guard_code !== "LLM_GUARD") {
        toast.error(e?.response?.data?.detail || "Couldn't generate the image. Try again.");
      }
    } finally { setGenerating(false); }
  };

  // "Dress the whole cube" — generate a UNIQUE, on-theme image for every face
  // in one go (background job; faces fill in live as each one finishes).
  const runDressWholeCube = async () => {
    if (!modelId) { toast.error("Pick an image model first."); return; }
    if (modelUnavailable) { popImageGuard(); return; }
    dressSnapRef.current = snapshotFaces(effectiveFaces.map((f) => f.id));
    setGenAll(true);
    setGenAllProgress({ done: 0, total: effectiveFaces.length });
    try {
      const r = await api.post(`/silos/${silo.id}/image/generate-all`, {
        prompt: description, model: modelId, edge_style: edgeStyle, deviation,
        only_empty: fillOnly,
      });
      if (r.data?.nothing_to_do) {
        setGenAll(false);
        toast("Every face already has a photo — nothing to fill. Uncheck \u201cFill blanks only\u201d to re-dress them all.", { duration: 6000 });
        return;
      }
      const jobId = r.data?.job_id;
      setGenAllProgress({ done: 0, total: r.data?.total || 10 });
      stopPoll();
      let pollFails = 0;
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/silos/${silo.id}/image/generate-all/${jobId}`);
          pollFails = 0;
          const job = s.data || {};
          const faces = job.faces || {};
          if (Object.keys(faces).length) {
            setLocalFaces((prev) => {
              const next = { ...prev };
              Object.entries(faces).forEach(([f, imgId]) => { next[f] = { image_id: imgId, edge_style: job.edge_style || edgeStyle }; });
              return next;
            });
            setBust(Date.now());
          }
          setGenAllProgress({ done: job.done || 0, total: job.total || 10 });
          if (job.status && job.status !== "running") {
            stopPoll();
            setGenAll(false);
            onSaved && onSaved();
            if (job.status === "done") toast.success("Dressed the whole cube — a unique image on every face.");
            else if (job.status === "partial") toast(`Set ${job.done} of ${job.total} faces. Tap again to fill the rest.`);
            else toast.error("Couldn't create the set. Check your key has image access, then try again.");
          }
        } catch {
          // Tolerate the odd dropped poll on mobile — only give up after several
          // misses in a row. The job keeps running on the server regardless.
          pollFails += 1;
          if (pollFails >= 6) {
            stopPoll();
            setGenAll(false);
            toast("Still working in the background — close this and check back in a few minutes; your photos will land on the faces as they finish.", { duration: 8000 });
          }
        }
      }, 2000);
    } catch (e) {
      setGenAll(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (e?.response?.data?.guard_code === "LLM_GUARD") {
        // no key + empty matching voucher → the global guard CTA modal handles it
      } else if (status === 402 || status === 400) {
        // a real, actionable problem (missing/invalid key, bad model)
        toast.error(typeof detail === "string" ? detail : "Couldn't start the set.");
      } else {
        // slow start / timeout — the job has very likely kicked off anyway
        toast("This can take a few minutes. You can close this — your photos will appear on the faces as they finish, so check back shortly.", { duration: 8000 });
      }
    }
  };

  // Jun 2026 (Randy) — voucher users pay a 3× markup, and dressing the whole
  // cube fires ~a dozen image generations at once. Warn them + offer the BYOK
  // savings path BEFORE spending. BYOK users go straight through.
  const dressFaceCount = () => {
    if (!fillOnly) return effectiveFaces.length;
    const empty = effectiveFaces.filter((f) => !(localFaces?.[f.id]?.image_id)).length;
    return empty || effectiveFaces.length;
  };
  const dressWholeCube = () => {
    if (!modelId) { toast.error("Pick an image model first."); return; }
    if (modelUnavailable) { popImageGuard(); return; }
    if (selectedModel?.via === "voucher") { setDressConfirmOpen(true); return; }
    runDressWholeCube();
  };

  const toggleAlwaysLabel = async (v) => {
    setAlwaysLabel(v);
    setSavingLabel(true);
    try {
      await api.patch(`/silos/${silo.id}/cube/label`, { always_show_label: v });
      onSaved && onSaved();
    } catch {
      setAlwaysLabel(!v);
      toast.error("Couldn't save that setting.");
    } finally { setSavingLabel(false); }
  };

  // ---- Undo + Remove-border ----
  const snapshotFaces = (ids) => {
    const snap = {};
    ids.forEach((id) => {
      const cur = localFaces?.[id];
      snap[id] = cur?.image_id ? { image_id: cur.image_id, edge_style: cur.edge_style || "clean" } : null;
    });
    return snap;
  };
  const restoreFaces = async (snap) => {
    if (!snap) return;
    setLocalFaces((prev) => {
      const next = { ...prev };
      Object.entries(snap).forEach(([id, v]) => { if (v) next[id] = { ...v }; else delete next[id]; });
      return next;
    });
    setBust(Date.now());
    try { await api.patch(`/silos/${silo.id}/cube/faces`, { faces: snap }); onSaved && onSaved(); }
    catch { toast.error("Couldn't undo. Refresh to check."); }
  };
  const restoreEdge = async (face, edge, amount, variance) => {
    const amt = typeof amount === "number" ? amount : edgeAmountDefault(edge);
    const vr = typeof variance === "number" ? variance : edgeVarianceDefault(edge);
    setLocalFaces((p) => ({ ...p, [face]: { ...(p[face] || {}), edge_style: edge, edge_amount: amt, edge_variance: vr } }));
    try { await api.patch(`/silos/${silo.id}/cube/face-edge`, { face, edge_style: edge, edge_amount: amt, edge_variance: vr }); onSaved && onSaved(); } catch { /* */ }
  };
  const removeBorder = async () => {
    const face = targetFace;
    const cur = localFaces?.[face];
    if (!cur?.image_id) { toast.error("That face has no image yet."); return; }
    const prevEdge = cur.edge_style || "clean";
    if (prevEdge === "clean") { toast("That face already has no border."); return; }
    setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: "clean" } }));
    try {
      await api.patch(`/silos/${silo.id}/cube/face-edge`, { face, edge_style: "clean" });
      onSaved && onSaved();
      toast.success("Border removed.", { action: { label: "Undo", onClick: () => restoreEdge(face, prevEdge) } });
    } catch {
      setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: prevEdge } }));
      toast.error("Couldn't remove the border.");
    }
  };
  // Remove border for a SPECIFIC face (used from inside the framer).
  const removeBorderForFace = async (face) => {
    const cur = localFaces?.[face];
    if (!cur?.image_id) return;
    const prevEdge = cur.edge_style || "clean";
    if (prevEdge === "clean") { toast("That face already has no border."); return; }
    setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: "clean" } }));
    try {
      await api.patch(`/silos/${silo.id}/cube/face-edge`, { face, edge_style: "clean" });
      onSaved && onSaved();
      toast.success("Border removed.", { action: { label: "Undo", onClick: () => restoreEdge(face, prevEdge) } });
    } catch {
      setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: prevEdge } }));
      toast.error("Couldn't remove the border.");
    }
  };
  // Recreate a face's image from AI (from inside the framer), using the current
  // description / model / that face's own frame. Updates the open framer live.
  const recreateFace = async (face) => {
    if (!modelId) { toast.error("Pick an image model first."); return; }
    if (modelUnavailable) { toast.error("Add that provider's key in Operations to use this model."); return; }
    setFramerRecreating(true);
    const snap = snapshotFaces([face]);
    try {
      const r = await api.post(`/silos/${silo.id}/image/generate`, {
        prompt: description, model: modelId, edge_style: localFaces?.[face]?.edge_style || edgeStyle, deviation, face, reuse_for_all: false,
      });
      const newId = r.data?.image_id;
      const t = Date.now();
      setLocalFaces((p) => ({ ...p, [face]: { ...(p[face] || {}), image_id: newId, crop: null } }));
      setBust(t);
      setFramer((fr) => (fr ? { ...fr, url: `${BACKEND_URL}/api/silo-images/${newId}?t=${t}`, crop: null } : fr));
      onSaved && onSaved();
      toast.success("Recreated from AI.", { action: { label: "Undo", onClick: () => restoreFaces(snap) } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't recreate the image. Try again.");
    } finally { setFramerRecreating(false); }
  };

  // ---- Double-tap framer: zoom / pan an oversized image inside the square ----
  const openFramer = (faceId, allowEmpty = false) => {
    const hasImg = !!localFaces?.[faceId]?.image_id;
    if (!hasImg && !allowEmpty) return; // nothing to frame yet — stay quiet (no toast)
    const f = STUDIO_FACES.find((x) => x.id === faceId);
    // Link the framer to the studio: same target face + same edge value, so the
    // "Frame the image" dialog and the "Set up cube images" screen stay in sync.
    setTargetFace(faceId);
    const ff = localFaces?.[faceId];
    const e0 = KNOWN_EDGES.has(ff?.edge_style) ? ff.edge_style : "clean";
    setEdgeStyle(e0);
    setEdgeAmount(typeof ff?.edge_amount === "number" ? ff.edge_amount : edgeAmountDefault(e0));
    setEdgeVariance(typeof ff?.edge_variance === "number" ? ff.edge_variance : edgeVarianceDefault(e0));
    setFramer({ face: faceId, url: hasImg ? faceUrl(faceId) : null, label: (f && f.label) || faceId, crop: localFaces[faceId]?.crop || null });
  };
  // Prev/Next through EVERY face right inside the framer (Randy) — cycles the
  // full cube order (15 faces, 16 for the founder); empty faces open ready to
  // Replace / Recreate.
  const framerFaceIds = effectiveFaces.map((f) => f.id);
  const framerNav = (dir) => {
    const idx = framerFaceIds.indexOf(framer?.face);
    if (idx < 0) return;
    const n = framerFaceIds.length;
    openFramer(framerFaceIds[(idx + dir + n) % n], true);
  };
  const framerFaceIndex = framer ? framerFaceIds.indexOf(framer.face) : -1;
  const saveCrop = async (crop) => {
    const face = framer?.face;
    if (!face) return;
    setLocalFaces((p) => ({ ...p, [face]: { ...(p[face] || {}), crop } }));
    setFramer(null);
    setBust(Date.now());
    try { await api.patch(`/silos/${silo.id}/cube/face-crop`, { face, crop }); onSaved && onSaved(); }
    catch { toast.error("Couldn't save the framing."); }
  };
  // Replace the photo from INSIDE the framer (keeps the face + frame, swaps the
  // picture, resets the framing for the fresh photo).
  const onFramerFile = async (e) => {
    const file = e.target.files?.[0];
    const face = framer?.face;
    if (!file || !face) return;
    setFramerUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("edge_style", localFaces?.[face]?.edge_style || "clean");
      fd.append("face", face);
      fd.append("reuse_for_all", "false");
      const r = await api.post(`/silos/${silo.id}/image/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = r.data?.image_id;
      const t = Date.now();
      setLocalFaces((p) => ({ ...p, [face]: { ...(p[face] || {}), image_id: newId, crop: null } }));
      setBust(t);
      setFramer((fr) => (fr ? { ...fr, url: `${BACKEND_URL}/api/silo-images/${newId}?t=${t}`, crop: null } : fr));
      onSaved && onSaved();
      toast.success("Photo replaced.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't replace the photo.");
    } finally { setFramerUploading(false); if (e.target) e.target.value = ""; }
  };

  // ---- Select a face: sync the Edge dropdown to that face's saved frame so the
  // preview reflects reality, then let the user change + Apply. ----
  const selectFace = (id) => {
    setTargetFace(id);
    const f = localFaces?.[id];
    if (f?.image_id && f.edge_style) {
      const e = KNOWN_EDGES.has(f.edge_style) ? f.edge_style : "clean";
      setEdgeStyle(e);
      setEdgeAmount(typeof f.edge_amount === "number" ? f.edge_amount : edgeAmountDefault(e));
      setEdgeVariance(typeof f.edge_variance === "number" ? f.edge_variance : edgeVarianceDefault(e));
    }
  };
  // Pick an edge from the dropdown: reset its slider(s) to the saved value for
  // this face (if it's the same edge) or that edge's sensible default.
  const pickEdge = (ed) => {
    const e = KNOWN_EDGES.has(ed) ? ed : "clean";
    const f = localFaces?.[targetFace];
    const same = f && f.edge_style === e;
    setEdgeStyle(e);
    setEdgeAmount(same && typeof f.edge_amount === "number" ? f.edge_amount : edgeAmountDefault(e));
    setEdgeVariance(same && typeof f.edge_variance === "number" ? f.edge_variance : edgeVarianceDefault(e));
  };
  const savedFace = localFaces?.[targetFace] || {};
  const savedEdge = savedFace.edge_style || "clean";
  const savedAmount = typeof savedFace.edge_amount === "number" ? savedFace.edge_amount : edgeAmountDefault(savedEdge);
  const savedVariance = typeof savedFace.edge_variance === "number" ? savedFace.edge_variance : edgeVarianceDefault(savedEdge);
  const frameChanged = !!savedFace.image_id && (edgeStyle !== savedEdge || edgeAmount !== savedAmount || edgeVariance !== savedVariance);
  const applyFrame = async () => {
    const face = targetFace;
    const prev = { e: localFaces?.[face]?.edge_style || "clean", a: localFaces?.[face]?.edge_amount, v: localFaces?.[face]?.edge_variance };
    setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: edgeStyle, edge_amount: edgeAmount, edge_variance: edgeVariance } }));
    try {
      await api.patch(`/silos/${silo.id}/cube/face-edge`, { face, edge_style: edgeStyle, edge_amount: edgeAmount, edge_variance: edgeVariance });
      onSaved && onSaved();
      toast.success("Edge applied.", { action: { label: "Undo", onClick: () => restoreEdge(face, prev.e, prev.a, prev.v) } });
    } catch {
      setLocalFaces((p) => ({ ...p, [face]: { ...p[face], edge_style: prev.e, edge_amount: prev.a, edge_variance: prev.v } }));
      toast.error("Couldn't apply the edge.");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const snap = snapshotFaces(reuseAll ? effectiveFaces.map((f) => f.id) : [targetFace]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("edge_style", edgeStyle);
      fd.append("face", targetFace);
      fd.append("reuse_for_all", reuseAll ? "true" : "false");
      const r = await api.post(`/silos/${silo.id}/image/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyResult(r.data?.image_id, reuseAll);
      toast.success(reuseAll ? "Photo uploaded and set on every face." : `Photo set on the ${faceLabel} face.`, { action: { label: reuseAll ? "Undo all" : "Undo", onClick: () => restoreFaces(snap) } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); if (e.target) e.target.value = ""; }
  };

  const labelCol = "text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] w-24 shrink-0";
  const selectedHasImage = !!localFaces?.[targetFace]?.image_id;
  const upFaces = STUDIO_LAYOUT.up.filter((f) => !f.founderOnly || isFounder);
  const downFaces = STUDIO_LAYOUT.down.filter((f) => !f.founderOnly || isFounder);

  const renderFace = (f, big = false) => {
    const sel = targetFace === f.id;
    const url = faceUrl(f.id);
    return (
      <button
        key={f.id}
        type="button"
        onClick={() => selectFace(f.id)}
        onDoubleClick={() => openFramer(f.id)}
        data-testid={`cube-face-square-${f.id}`}
        title={f.label}
        style={{ touchAction: "manipulation" }}
        className={`relative shrink-0 ${big ? "w-24 h-24" : "w-20 h-20"} rounded-lg overflow-hidden transition-all ${sel ? "border-4 border-[#4A5D4E] ring-4 ring-[#4A5D4E]/50 shadow-lg scale-105" : "border-2 border-transparent hover:border-[#C9C7BF]"}`}
      >
        {url ? (
          <div className="cube-edge w-full h-full" data-edge={localFaces?.[f.id]?.edge_style || "clean"} style={edgeRenderStyle(localFaces?.[f.id]?.edge_style || "clean", localFaces?.[f.id]?.edge_amount, localFaces?.[f.id]?.edge_variance).container}>
            <img src={url} alt={f.label} className="cube-edge-img" style={{ ...cropStyle(localFaces?.[f.id]?.crop), ...edgeRenderStyle(localFaces?.[f.id]?.edge_style || "clean", localFaces?.[f.id]?.edge_amount, localFaces?.[f.id]?.edge_variance).img }} />
            {edgeRenderStyle(localFaces?.[f.id]?.edge_style || "clean", localFaces?.[f.id]?.edge_amount, localFaces?.[f.id]?.edge_variance).overlay && <div className="absolute inset-0 pointer-events-none" data-no-invert style={edgeRenderStyle(localFaces?.[f.id]?.edge_style || "clean", localFaces?.[f.id]?.edge_amount, localFaces?.[f.id]?.edge_variance).overlay} />}
          </div>
        ) : (
          <div className="w-full h-full bg-[#E7E5DD]" />
        )}
        {sel && (
          <span className="absolute top-1 right-1 text-[#4A5D4E] bg-white rounded-full leading-none" data-no-invert data-testid={`cube-face-selected-${f.id}`}>
            <CheckCircle size={18} weight="fill" />
          </span>
        )}
        <span className={`absolute bottom-0 inset-x-0 text-white text-[8px] uppercase tracking-[0.1em] py-0.5 text-center leading-tight ${sel ? "bg-[#4A5D4E]" : "bg-black/45"}`} data-no-invert>{f.label}</span>
      </button>
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-none w-screen h-[100dvh] rounded-none border-0 p-0 flex flex-col gap-0" data-testid="silo-image-studio">
        <DialogHeader className="px-5 sm:px-8 pt-5 pb-3 border-b hairline shrink-0">
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-[#4A5D4E]" />
            Set up cube images — {silo?.name}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B6A68]">
            Build a face's image with AI from a short description, or upload a photo. Pick a face below, choose an edge and how far the AI may roam, then create.
          </DialogDescription>
        </DialogHeader>

        {/* Jul 2026 (Randy) — split into Stage 1 (cube art, today) and
            Stage 2 (Silo Art V2 "Elevated Mode"). */}
        <div className="px-5 sm:px-8 pt-3 border-b hairline shrink-0 flex gap-1" data-testid="silo-art-stage-tabs">
          {[["stage1", "Silo Art · Stage 1"], ["stage2", "Silo Art · Stage 2"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setStudioStage(id)}
              data-testid={`studio-stage-${id}`}
              className={`px-4 py-2.5 text-sm rounded-t-md -mb-px border-b-2 transition-colors ${studioStage === id ? "border-[#4A5D4E] text-[#1C1B1A] font-medium" : "border-transparent text-[#6B6A68] hover:text-[#1C1B1A]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {studioStage === "stage2" && (
          <div className="flex-1 overflow-y-auto px-5 sm:px-8 pb-5 pt-0 max-w-3xl w-full mx-auto" data-testid="studio-stage2-body">
            <SiloArtStage2Panel silo={silo} onSaved={onSaved} />
          </div>
        )}

        {/* TOP: controls */}
        <div className={`flex-1 overflow-y-auto px-5 sm:px-8 py-5 space-y-4 max-w-3xl w-full mx-auto ${studioStage === "stage1" ? "" : "hidden"}`}>
          <div className="flex items-center gap-3">
            <label className={labelCol}>Source</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-full" data-testid="cube-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ai" data-testid="cube-mode-ai">AI — generate an image</SelectItem>
                <SelectItem value="upload" data-testid="cube-mode-upload">Upload from phone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === "ai" && (
            <>
              <div className="flex items-start gap-3">
                <label className={`${labelCol} mt-2`}>Describe</label>
                <button
                  type="button"
                  onClick={() => setDescribeOpen(true)}
                  data-testid="cube-describe-open"
                  className="flex-1 text-left px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] text-sm text-[#1C1B1A] transition-colors"
                >
                  {description ? <span className="line-clamp-2">{description}</span> : <span className="text-[#6B6A68]">Tap to type or dictate what you picture…</span>}
                </button>
              </div>
              <div className="flex items-center gap-3">
                <label className={labelCol}>Edge</label>
                <Select value={edgeStyle} onValueChange={pickEdge}>
                  <SelectTrigger className="w-full" data-testid="cube-edge-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {edges.map((ed) => <SelectItem key={ed.id} value={ed.id} data-testid={`cube-edge-${ed.id}`}>{ed.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-[108px]">
                <EdgeControls edge={edgeStyle} amount={edgeAmount} variance={edgeVariance}
                  onChange={({ amount, variance }) => { setEdgeAmount(amount); setEdgeVariance(variance); }} />
              </div>
              <div className="flex items-center gap-3">
                <label className={labelCol}>Model</label>
                <Select value={modelId} onValueChange={setModelId}>
                  <SelectTrigger className="w-full" data-testid="cube-model-select"><SelectValue placeholder="Pick a model" /></SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m.id} value={m.id} disabled={!m.available} data-testid={`cube-model-${m.id}`}>
                        {m.label} · ~{m.approx_cents}¢{m.available ? "" : " · add key"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {modelUnavailable && (
                <p className="text-xs text-[#C16E5A] ml-[108px]" data-testid="cube-model-unavailable">
                  Add your {selectedModel.provider === "gemini" ? "Google Gemini" : "OpenAI"} key in Operations → Bring your own key to use this model.
                </p>
              )}
              {selectedModel && <p className="text-xs text-[#6B6A68] ml-[108px]">{selectedModel.blurb}</p>}
              {selectedModel && selectedModel.available && (
                <p className="text-[10px] text-[#9b9a96] ml-[108px] -mt-0.5" data-testid="cube-cost-hint">
                  {selectedModel.via === "voucher"
                    ? `Running on your ${selectedModel.provider === "gemini" ? "Gemini" : "OpenAI"} voucher · ~${selectedModel.approx_cents * 3}¢/image`
                    : `On your ${selectedModel.provider === "gemini" ? "Gemini" : "OpenAI"} key · ~${selectedModel.approx_cents}¢/image`}
                </p>
              )}
              <div className="flex items-center gap-3">
                <label className={labelCol}>Imagination</label>
                <div className="flex-1">
                  <Slider value={[deviation]} onValueChange={([v]) => setDeviation(v)} min={0} max={100} step={5} data-testid="cube-deviation-slider" />
                  <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mt-1">
                    <span>Faithful</span><span className="tabular-nums text-[#4A5D4E]">{deviation}</span><span>Bold</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {mode === "upload" && (
            <>
              <div className="flex items-center gap-3">
                <label className={labelCol}>Edge</label>
                <Select value={edgeStyle} onValueChange={pickEdge}>
                  <SelectTrigger className="w-full" data-testid="cube-edge-select-upload"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {edges.map((ed) => <SelectItem key={ed.id} value={ed.id}>{ed.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-[108px]">
                <EdgeControls edge={edgeStyle} amount={edgeAmount} variance={edgeVariance}
                  onChange={({ amount, variance }) => { setEdgeAmount(amount); setEdgeVariance(variance); }} />
              </div>
              <label className="flex items-center gap-2 ml-[108px] cursor-pointer" data-testid="cube-reuse-all-wrap">
                <Checkbox checked={reuseAll} onCheckedChange={(v) => setReuseAll(!!v)} data-testid="cube-reuse-all" />
                <span className="text-xs text-[#1C1B1A]">Use this one photo on every face</span>
              </label>
            </>
          )}

          <label className="flex items-center gap-2.5 ml-[108px] cursor-pointer pt-1" data-testid="cube-always-label-wrap">
            <Checkbox checked={alwaysLabel} onCheckedChange={(v) => toggleAlwaysLabel(!!v)} disabled={savingLabel} data-testid="cube-always-label" />
            <span className="text-xs text-[#1C1B1A]">Always show the name on the tile <span className="text-[#6B6A68]">— white, full-time</span></span>
          </label>

          {/* LIVE PREVIEW — exactly how the tile + frame will look before you commit */}
          <div className="pt-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-2 text-center">Preview — {faceLabel} face</p>
            <div
              className="relative w-full max-w-sm mx-auto rounded-xl overflow-hidden bg-[#1C1B1A] border hairline cursor-pointer"
              style={{ aspectRatio: "540 / 184" }}
              data-testid="cube-preview"
              onDoubleClick={() => openFramer(targetFace)}
              title="Double-tap to frame this photo"
            >
              {faceUrl(targetFace) ? (
                <>
                  <div className="cube-edge w-full h-full" data-edge={edgeStyle} style={edgeRenderStyle(edgeStyle, edgeAmount, edgeVariance).container}>
                    <img src={faceUrl(targetFace)} alt="tile preview" className="cube-edge-img" style={{ ...cropStyle(localFaces?.[targetFace]?.crop), ...edgeRenderStyle(edgeStyle, edgeAmount, edgeVariance).img }} />
                    {edgeRenderStyle(edgeStyle, edgeAmount, edgeVariance).overlay && <div className="absolute inset-0 pointer-events-none" data-no-invert style={edgeRenderStyle(edgeStyle, edgeAmount, edgeVariance).overlay} />}
                  </div>
                  {alwaysLabel && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="font-serif-display text-3xl font-semibold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]">{silo?.name}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-[#6B6A68] px-4 text-center">
                  Pick a face that has an image to preview the frame
                </div>
              )}
            </div>
            {frameChanged && (
              <p className="text-[11px] text-[#4A5D4E] text-center mt-2">New frame — tap <span className="font-medium">Apply frame</span> below to keep it.</p>
            )}
            <CubeNavControls isFounder={isFounder} />
          </div>
        </div>

        {/* BOTTOM: the face map — collapsible so mobile can see the preview + dials above */}
        {facesCollapsed ? (
          <button
            type="button"
            onClick={() => setFacesCollapsed(false)}
            data-testid="cube-faces-expand"
            className={`border-t hairline bg-[#F7F6F1] shrink-0 w-full py-2.5 pb-[calc(env(safe-area-inset-bottom)+0.6rem)] flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] hover:text-[#1C1B1A] ${studioStage === "stage1" ? "" : "hidden"}`}
          >
            <CaretUp size={12} weight="bold" /> Show faces
          </button>
        ) : (
        <div className={`relative border-t hairline bg-[#F7F6F1] px-5 sm:px-8 pt-3 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] sm:pb-5 shrink-0 ${studioStage === "stage1" ? "" : "hidden"}`}>
          <button
            type="button"
            onClick={() => setFacesCollapsed(true)}
            data-testid="cube-faces-collapse"
            title="Minimize this section"
            className="absolute top-1 right-3 p-1 text-[#6B6A68] hover:text-[#1C1B1A] z-10"
          >
            <Minus size={18} weight="bold" />
          </button>
          <div className="flex items-center justify-between gap-3 mb-2 max-w-5xl mx-auto flex-wrap pr-6">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Tap a face — its image &amp; border land there</p>
            <div className="flex items-center gap-2 flex-wrap">
              {selectedHasImage && (
                <Button onClick={removeBorder} variant="ghost" data-testid="cube-remove-border-btn" className="text-[#6B6A68] hover:text-[#1C1B1A] px-2">
                  <X size={14} className="mr-1" /> Remove border
                </Button>
              )}
              {frameChanged && (
                <Button onClick={applyFrame} variant="outline" data-testid="cube-apply-frame-btn" className="border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#EEF1ED]">
                  <CheckCircle size={14} weight="bold" className="mr-1" /> Apply frame
                </Button>
              )}
              {mode === "ai" ? (
                <>
                  <Button onClick={generate} disabled={generating || genAll || modelUnavailable} data-testid="cube-generate-btn" variant="outline" className="border-[#1C1B1A] text-[#1C1B1A] hover:bg-[#F0F1EE]">
                    {generating ? <><Spinner size={16} className="animate-spin mr-1.5" /> Creating…</> : <><Sparkle size={16} weight="duotone" className="mr-1.5" /> Set this face</>}
                  </Button>
                  <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-[#1C1B1A] mr-0.5" data-testid="cube-fill-only-wrap" title="Keep the photos you've already set — only fill the empty faces, never overwrite.">
                    <Checkbox checked={fillOnly} onCheckedChange={(v) => setFillOnly(!!v)} data-testid="cube-fill-only" />
                    <span className="leading-tight">Fill blanks<br />only</span>
                  </label>
                  <Button onClick={dressWholeCube} disabled={genAll || generating || modelUnavailable} data-testid="cube-generate-all-btn" className="bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E]">
                    {genAll ? <><Spinner size={16} className="animate-spin mr-1.5" /> Dressing… {genAllProgress.done}/{genAllProgress.total}</> : <><PaintBrush size={16} weight="duotone" className="mr-1.5" /> Dress the whole cube</>}
                  </Button>
                </>
              ) : (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="cube-upload-input" />
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="cube-upload-btn" className="bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]">
                    {uploading ? <><Spinner size={16} className="animate-spin mr-1.5" /> Uploading…</> : <><UploadSimple size={16} weight="bold" className="mr-1.5" /> Choose photo</>}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Above home (click up): Public Q&A → Account & Billing → Admin */}
          <div className="flex items-center justify-center gap-2.5 mb-2 max-w-5xl mx-auto">
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#9b9a96] flex items-center w-16 justify-end text-right leading-tight" data-no-invert>Click up</span>
            {upFaces.map((f) => renderFace(f))}
          </div>

          {/* Home + AI Assistant + tabs (swipe left / right) */}
          <div className="flex gap-2.5 overflow-x-auto pb-1 max-w-5xl mx-auto items-center [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="cube-face-strip">
            {STUDIO_LAYOUT.mid.map((f) => renderFace(f, f.id === "center"))}
          </div>

          {/* Below home (click down): Fan Portal → Silo Wars */}
          <div className="flex items-center justify-center gap-2.5 mt-2 max-w-5xl mx-auto flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#9b9a96] flex items-center w-16 justify-end text-right leading-tight" data-no-invert>Click down</span>
            {downFaces.map((f) => renderFace(f))}
          </div>
        </div>
        )}

        {/* Describe popup */}
        {describeOpen && (
          <div className="absolute inset-0 z-30 bg-[#FDFCF8] p-6 flex flex-col max-w-3xl w-full mx-auto" data-testid="cube-describe-popup">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif-display text-xl text-[#1C1B1A]">Describe your image</h3>
              <button type="button" onClick={() => setDescribeOpen(false)} data-testid="cube-describe-close" className="p-1.5 rounded-md hover:bg-[#F0F1EE] text-[#6B6A68]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#6B6A68] mb-3">This is what the AI builds from. Type it, dictate it, or let AI draft one from your Tier 1 content.</p>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. A serene cosmos of soft indigo nebulae and golden constellations, calm and contemplative…" className="flex-1 min-h-[140px] resize-none" data-testid="cube-describe-textarea" />
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button type="button" variant="outline" onClick={toggleMic} data-testid="cube-describe-mic" className={listening ? "border-[#C16E5A] text-[#C16E5A]" : ""}>
                <Microphone size={16} weight={listening ? "fill" : "duotone"} className="mr-1.5" />{listening ? "Listening… tap to stop" : "Dictate"}
              </Button>
              <Button type="button" variant="outline" onClick={draftFromTier1} disabled={drafting} data-testid="cube-describe-draft">
                {drafting ? <Spinner size={16} className="animate-spin mr-1.5" /> : <MagicWand size={16} weight="duotone" className="mr-1.5" />}Draft from my Tier 1
              </Button>
              <div className="flex-1" />
              <Button type="button" onClick={() => setDescribeOpen(false)} data-testid="cube-describe-save" className="bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E]">Save</Button>
            </div>
          </div>
        )}
        {/* Jun 2026 (Randy) — rendered INSIDE DialogContent so it lives in the
            Radix portal; otherwise Radix's modal pointer-events trap makes
            every button (Switch / Not now) dead on click. */}
        {dressConfirmOpen && (() => {
          const count = dressFaceCount();
          const byokC = (selectedModel?.approx_cents || 4) * count;
          const vouchC = byokC * 3;
          const provName = selectedModel?.provider === "gemini" ? "Gemini" : "OpenAI";
          const fmt = (c) => (c >= 100 ? `$${(c / 100).toFixed(2)}` : `${c}\u00A2`);
          return (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="dress-voucher-confirm">
              <div className="absolute inset-0 bg-[#1C1B1A]/55 backdrop-blur-sm" onClick={() => setDressConfirmOpen(false)} />
              <div className="modal-safe relative z-10 bg-[#FDFCF8] rounded-2xl max-w-md w-full shadow-2xl border hairline p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 text-[#9A4A38] mb-2">
                  <PaintBrush size={18} weight="duotone" />
                  <h3 className="font-serif-display text-xl text-[#1C1B1A]">Dressing the whole cube adds up on vouchers</h3>
                </div>
                <p className="text-sm text-[#4A4A48] leading-relaxed">
                  This creates a unique image for <strong>~{count} faces</strong> in one go. On your{" "}
                  {provName} <strong>voucher</strong> that's about <strong>{fmt(vouchC)}</strong>
                  {" "}(a 3× platform markup).
                </p>
                <div className="mt-3 rounded-lg border border-[#2F8F4E]/40 bg-[#E9F3EC] p-3 text-sm text-[#2F6B3A]">
                  With your own {provName} key it'd be about <strong>{fmt(byokC)}</strong> — roughly a third
                  of the cost. Example: <strong>{fmt(byokC)}</strong> on your key vs <strong>{fmt(vouchC)}</strong> on vouchers.
                </div>
                <div className="mt-5 flex flex-col gap-2">
                  <button
                    data-testid="dress-confirm-proceed"
                    onClick={() => { setDressConfirmOpen(false); runDressWholeCube(); }}
                    className="w-full px-5 py-3 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors text-sm font-medium"
                  >
                    Proceed on voucher (~{fmt(vouchC)})
                  </button>
                  <button
                    data-testid="dress-confirm-switch-byok"
                    onClick={() => { window.location.href = "/dashboard/account-settings?focus=byok"; }}
                    className="w-full px-5 py-2.5 rounded-md border border-[#2F8F4E] text-[#2F6B3A] hover:bg-[#E9F3EC] transition-colors text-sm font-medium"
                  >
                    Set up my own {provName} key ~ cost {fmt(byokC)} →
                  </button>
                  <button
                    data-testid="dress-confirm-cancel"
                    onClick={() => setDressConfirmOpen(false)}
                    className="w-full px-5 py-2 text-[#6B6A68] hover:text-[#1C1B1A] transition-colors text-sm"
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
    <SiloFaceFramer
      open={!!framer}
      imageUrl={framer?.url}
      faceLabel={framer?.label}
      initialCrop={framer?.crop}
      description={description}
      onDescriptionChange={setDescription}
      onReplacePhoto={() => framerFileRef.current?.click()}
      replacing={framerUploading}
      onDraftFromTier1={draftFromTier1}
      drafting={drafting}
      onRecreate={() => framer && recreateFace(framer.face)}
      recreating={framerRecreating}
      onRemoveBorder={() => framer && removeBorderForFace(framer.face)}
      hasBorder={!!framer && (localFaces?.[framer.face]?.edge_style || "clean") !== "clean"}
      edges={edges}
      edge={edgeStyle}
      edgeAmount={edgeAmount}
      edgeVariance={edgeVariance}
      onEdgeChange={(ed, amount, variance) => {
        setEdgeStyle(ed);
        if (typeof amount === "number") setEdgeAmount(amount);
        if (typeof variance === "number") setEdgeVariance(variance);
        if (framer) restoreEdge(framer.face, ed, amount, variance);
      }}
      onSave={saveCrop}
      onClose={() => setFramer(null)}
      onPrevFace={() => framerNav(-1)}
      onNextFace={() => framerNav(1)}
      faceIndex={framerFaceIndex >= 0 ? framerFaceIndex + 1 : 1}
      faceCount={framerFaceIds.length}
    />
    <input ref={framerFileRef} type="file" accept="image/*" className="hidden" onChange={onFramerFile} data-testid="framer-upload-input" />
    </>
  );
}
